import { getRecordActionItems } from '@/features/RecordActions/model/recordActionItems';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserDialogStore } from '@/entities/UserDialog/model/store';
import { DIALOG_STATUS_NAMES } from '@/entities/UserDialog/config/dialog.config';
import { RequestStatus } from '@/shared/model/requestStatus';
import { DataTable } from '@/shared/ui/DataTable/DataTable';
import { ActionMenu } from '@/shared/ui/ActionMenu/ActionMenu';
import { BackButton } from '@/shared/ui/BackButton/BackButton';
import { EntityActionPanel } from '@/shared/ui/EntityActionPanel/EntityActionPanel';
import { Link } from '@/shared/ui/Link/Link';
import buttonClasses from '@/styles/buttons.module.scss';
import { DropdownField } from '@/shared/ui/DropdownField/DropdownField';
import { RequestEmpty, RequestErrorBanner, RequestLoading } from '@/shared/ui/RequestState';
import styles from './UserDialogs.module.scss';

/** Показывает общую статистику выпуска и страницу назначений */
export const DialogBroadcastDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { details, loadDetails } = useUserDialogStore();
    const [first, setFirst] = useState(0);
    const [answer, setAnswer] = useState<string>('all');

    const request = {
        skipCount: first,
        takeCount: 10,
        hasAnswer: answer === 'all'
            ? undefined
            : answer === 'yes'
    };

    useEffect(() => {
        if (id) void loadDetails(id, {
            skipCount: first,
            takeCount: 10,
            hasAnswer: answer === 'all'
                ? undefined
                : answer === 'yes'
        });
    }, [id, first, answer, loadDetails]);

    const data = details.data?.broadcast?.id === id
        ? details.data
        : null;

    const statistics = data?.statistics;

    return <main className={styles.page}>
        <EntityActionPanel
            name={data?.broadcast?.name ?? 'Статистика выпуска'}
            buttons={<div className={buttonClasses.buttonGroup}>
                <BackButton
                    url="/user-dialogs"
                    title="К списку"
                />
                <ActionMenu
                    disabled={details.status === RequestStatus.Loading}
                    model={getRecordActionItems({ kind: 'dialogBroadcasts' }, {
                        refresh: () => { if (id) void loadDetails(id, request); },
                        openTemplate: data?.broadcast?.templateId
                            ? () => navigate(`/user-dialogs/templates/${data.broadcast?.templateId}`)
                            : undefined,
                    })}
                />
            </div>}
        />
        <RequestErrorBanner
            {...details}
            fallbackMessage="Не удалось загрузить статистику"
            onRetry={() => id
                ? loadDetails(id, request)
                : undefined}
        />
        {(details.status === RequestStatus.Loading || details.status === RequestStatus.Idle) &&
            <RequestLoading message="Загрузка статистики..." />
        }
        {details.status === RequestStatus.Success && !data &&
            <RequestEmpty message="Выпуск не найден" />
        }
        {data &&
            <>
                <p>{data.broadcast?.isSystem
                    ? 'Постоянный системный выпуск'
                    : 'Ручной выпуск'} · {data.broadcast?.filterDescription}</p>
                <Link
                    isBlank={false}
                    url={data.broadcast?.templateId
                        ? `/user-dialogs/templates/${data.broadcast.templateId}`
                        : null
                    }
                    text={`Шаблон: ${data.broadcast?.templateTitle ?? '—'}`}
                />
                <div className={styles.statistics}>
                    {[
                        ['Всего', statistics?.total], ['Ответили', statistics?.responded], ['Без ответа', statistics?.unanswered],
                        ['Ожидают', statistics?.pending], ['Прочитали', statistics?.read], ['Ответ на вопрос', statistics?.answered],
                        ['Истекли', statistics?.expired], ['Отменены', statistics?.cancelled],
                    ].map(([label, value]) => <div
                        key={label}
                        className={styles.stat}
                    >
                        <span>{label}</span><strong>{value ?? 0}</strong>
                    </div>)}
                </div>
                <h2>Ответы</h2>
                {statistics?.answers?.length
                    ? <ul>{statistics.answers.map((item, index) => <li key={`${item.optionType}-${index}`}>{item.text || 'Подтверждение'}: {item.count}</li>)}</ul>
                    : <p>Ответов пока нет</p>}
                <h2>Назначенные диалоги</h2>
                <DropdownField
                    name="answer"
                    label="Ответ пользователя"
                    value={answer}
                    options={[{
                        id: 'all',
                        name: 'Все'
                    }, {
                        id: 'yes',
                        name: 'Ответили'
                    }, {
                        id: 'no',
                        name: 'Без ответа'
                    }]}
                    showClear={false}
                    onChange={event => {
                        setAnswer(String(event.value));
                        setFirst(0);
                    }}
                />
                <DataTable
                    data={data.dialogs ?? []}
                    columns={[
                        {
                            field: 'name',
                            alignHeader: 'center',
                            bodyClassName: styles.centeredCell,
                            header: 'Получатель',
                            body: item => item.profileId
                                ? <Link
                                    isBlank={false}
                                    url={`/${item.audience === 'clients'
                                        ? 'clients'
                                        : 'executors'}/${item.profileId}`}
                                    text={item.name ?? '—'}
                                />
                                : item.name
                        },
                        {
                            field: 'phone',
                            alignHeader: 'center',
                            bodyClassName: styles.centeredCell,
                            header: 'Телефон'
                        },
                        {
                            field: 'status',
                            alignHeader: 'center',
                            bodyClassName: styles.centeredCell,
                            header: 'Статус',
                            body: item => item.status
                                ? DIALOG_STATUS_NAMES[item.status]
                                : '—'
                        },
                        {
                            field: 'answerText',
                            alignHeader: 'center',
                            bodyClassName: styles.centeredCell,
                            header: 'Ответ',
                            body: item => item.answerText ?? '—'
                        },
                        {
                            field: 'textAnswer',
                            alignHeader: 'center',
                            bodyClassName: styles.centeredCell,
                            header: 'Обратная связь',
                            body: item => <span className={styles.feedbackText}>{item.textAnswer ?? '—'}</span>
                        },
                    ]}
                    paginator
                    lazy
                    first={first}
                    rows={10}
                    rowsPerPageOptions={[10]}
                    totalRecords={data.allCount ?? 0}
                    onPageChange={event => setFirst(event.first)}
                    loading={details.status === RequestStatus.Loading}
                />
            </>
        }
    </main>;
};
